import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../../../utils/Buttons';
import { LoadingUI } from '../../../../utils/LayoutStyles';

import { CustomAutocomplete, CustomDatePicker, CustomTextField } from '../../../../utils/StyledInputs';
import genderList from '../../../../model/gender';

import API from '../../../../api';
import { EntitiesDialog } from '../../../../utils/EntitiesDialog';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { validatePhoneNumber } from '../../../../utils/numberValidity';
import { usePopup } from '../../../../context/PopupContext';
import GdMasterEntitySelect from '../../../../components/registration/GdMasterEntitySelect';
import { isGdEligibleAge } from '../../../../components/registration/registrationConfig';
import { fetchGdEntities } from '../../../../services/tesseramento/gdEntitiesService';

export default function NewUserDialog({ openNewUser, setOpenNewUser, getUsers, mode }) {
  const navigate = useNavigate();
  const { showPopup, setPopUpText } = usePopup();

  /* -------------------------------- New User -------------------------------- */
  const [domRes, setDomRes] = useState(true);
  const [birthDate, setBirthDate] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [birthPlacesList, setBirthPlacesList] = useState([]);
  const fourteenYearsAgo = dayjs().subtract(14, 'year');
  const [gender, setGender] = useState('');
  const [callCode, setCallCode] = useState(null);
  const [entity, setEntity] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gdCheck, setGdCheck] = useState(false);
  const [isGdAvailable, setIsGdAvailable] = useState(false);
  const [entitiesFilterByResidence, setEntitiesFilterByResidence] = useState(true);
  const [disableEntity, setDisableEntity] = useState(false);

  // Residence
  const [resCountry, setResCountry] = useState('');
  const [resRegion, setResRegion] = useState('');
  const [resProvince, setResProvince] = useState('');
  const [resCity, setResCity] = useState('');
  const [resStreet, setResStreet] = useState('');
  const [resRegionName, setResRegionName] = useState(null);
  const [resCityName, setResCityName] = useState(null);

  // Domicile
  const [homeCountry, setHomeCountry] = useState('');
  const [homeRegion, setHomeRegion] = useState('');
  const [homeProvince, setHomeProvince] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeStreet, setHomeStreet] = useState('');
  const [homeRegionName, setHomeRegionName] = useState(null);
  const [homeCityName, setHomeCityName] = useState(null);

  // api static data
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [homeRegionsList, setHomeRegionsList] = useState([]);
  const [homeCountriesList, setHomeCountriesList] = useState([]);
  const [homeProvincesList, setHomeProvincesList] = useState([]);
  const [homeCitiesList, setHomeCitiesList] = useState([]);
  const [PositionsList, setPositionsList] = useState([]);
  const [EntitiesList, setEntitiesList] = useState([]);
  const [emptyEntitiesList, setEmptyEntitiesList] = useState(false);
  const [italy, setItaly] = useState(null);

  // Entità master GD (entity_master_gd): visibile solo per età eleggibile (14–29),
  // filtrata per residenza/domicilio come in registrazione/profilo.
  const [gdEntity, setGdEntity] = useState(null);
  const [gdEntityList, setGdEntityList] = useState([]);
  const [gdEntityListEmpty, setGdEntityListEmpty] = useState(false);

  // "È GD": l'admin ha spuntato l'iscrizione GD E l'età è eleggibile (14–29).
  // Il campo Entità GD è sempre visibile, ma attivo/obbligatorio solo in questo caso.
  const gdAge = birthDate ? dayjs().diff(dayjs(birthDate), 'year') : null;
  const isGdUser = gdCheck && isGdEligibleAge(gdAge);

  // Se l'utente non è GD, azzera l'entità GD eventualmente selezionata.
  useEffect(() => {
    if (!isGdUser) {
      setGdEntity(null);
    }
  }, [isGdUser]);

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    email: false,
    birth_date: false,
    place_of_birth: false,
    gender: false,
    name_alias: false,
    home_country_id: false,
    home_city_id: false,
    home_city_name: false,
    home_province_id: false,
    home_region_id: false,
    home_region_name: false,
    home_street: false,
    home_zip_code: false,
    call_code: false,
    res_street: false,
    fiscal_code: false,
    res_country_id: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_id: false,
    res_region_name: false,
    entity_id: false,
    entity_master_gd: false,
    res_zip_code: false,
  });

  // register api status
  const [registerApiCalled, setRegisterApiCalled] = useState(false);

  useEffect(() => {
    if (callCode !== null && callCode !== undefined && callCode !== '') {
      setResCountry(callCode);
      setHomeCountry(callCode);
    }

    if (PositionsList.length === 0) {
      getPositions();
    }

    if (birthPlacesList.length === 0) {
      const fetchData = async () => {
        try {
          const [citiesResponse] = await axios.all([API.get('/citiesfiscalcode')]);

          setBirthPlacesList(citiesResponse.data);
        } catch (error) {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        }
      };

      fetchData();
    }

    if (resCountriesList.length === 0) {
      // get contries
      const fetchData = async () => {
        try {
          const [countriesResponse] = await axios.all([API.get('/countries')]);

          setResCountriesList(countriesResponse.data);
          setHomeCountriesList(countriesResponse.data);
          const italyData = countriesResponse.data.find((element) => element.name === 'Italia');

          setItaly(italyData);
          setResCountry(italyData);
          setHomeCountry(italyData);
          setCallCode(italyData);
          setRegisterApiCalled(true);
        } catch (error) {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        }
      };

      fetchData();
    }
  }, [openNewUser]);

  // get positions
  const getPositions = () => {
    API.get('/positions/registration')
      .then((response) => {
        setPositionsList(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Get resRegionList
  useEffect(() => {
    if (openNewUser && resCountry !== null && resCountry !== undefined && resCountry !== '') {
      if (typeof resCountry === 'object' && Object.keys(resCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: resCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setResRegionList(response.data);
          } else {
            setResRegionList([]);
            setResProvincesList([]);
            setResCitiesList([]);
          }
        });
      }
    }
  }, [resCountry, openNewUser]);

  // Get resProvincesList
  useEffect(() => {
    if (resRegion !== null && resRegion !== undefined && resRegion !== '') {
      if (typeof resRegion === 'object' && Object.keys(resRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: resRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setResProvincesList(response.data);
          } else {
            setResProvincesList([]);
            setResCitiesList([]);
          }
        });
      }
    }
  }, [resRegion]);

  // Get resCitiesList
  useEffect(() => {
    if (resProvince !== null && resProvince !== undefined && resProvince !== '') {
      if (typeof resProvince === 'object' && Object.keys(resProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: resProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setResCitiesList(response.data);
          } else {
            setResCitiesList([]);
          }
        });
      }
    }
  }, [resProvince]);

  // Get homeRegionList
  useEffect(() => {
    if (homeCountry !== null && homeCountry !== undefined && homeCountry !== '') {
      if (typeof homeCountry === 'object' && Object.keys(homeCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: homeCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setHomeRegionsList(response.data);
          } else {
            setHomeRegionsList([]);
            setHomeProvincesList([]);
            setHomeCitiesList([]);
          }
        });
      }
    }
  }, [homeCountry]);

  // Get homeProvincesList
  useEffect(() => {
    if (homeRegion !== null && homeRegion !== undefined && homeRegion !== '') {
      if (typeof homeRegion === 'object' && Object.keys(homeRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: homeRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setHomeProvincesList(response.data);
          } else {
            setHomeProvincesList([]);
            setHomeCitiesList([]);
          }
        });
      }
    }
  }, [homeRegion]);

  // Get homeCitiesList
  useEffect(() => {
    if (homeProvince !== null && homeProvince !== undefined && homeProvince !== '') {
      if (typeof homeProvince === 'object' && Object.keys(homeProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: homeProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setHomeCitiesList(response.data);
          } else {
            setHomeCitiesList([]);
          }
        });
      }
    }
  }, [homeProvince]);

  useEffect(() => {
    setDisableEntity(true);
    if (
      entitiesFilterByResidence &&
      resCity !== null &&
      resCity !== undefined &&
      resCity !== '' &&
      resProvince !== null &&
      resProvince !== undefined &&
      resProvince !== ''
    ) {
      API.get(`/entities/registration?city_id=${resCity.id}&province_id=${resProvince.id}`).then((response) => {
        setEntitiesList(response.data.entities);
        setEmptyEntitiesList(response.data.empty);
        setDisableEntity(false);
      });
    } else if (
      entitiesFilterByResidence &&
      resCountry !== null &&
      resCountry !== undefined &&
      resCountry !== '' &&
      resCountry.id !== italy.id
    ) {
      API.get(`/entities/abroad_registration?id=${resCountry?.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else if (
      !entitiesFilterByResidence &&
      homeCity !== null &&
      homeCity !== undefined &&
      homeCity !== '' &&
      homeProvince !== null &&
      homeProvince !== undefined &&
      homeProvince !== ''
    ) {
      API.get(`/entities/registration?city_id=${homeCity.id}&province_id=${homeProvince.id}`).then((response) => {
        setEntitiesList(response.data.entities);
        setEmptyEntitiesList(response.data.empty);
        setDisableEntity(false);
      });
    } else if (
      !entitiesFilterByResidence &&
      homeCountry !== null &&
      homeCountry !== undefined &&
      homeCountry !== '' &&
      homeCountry.id !== italy.id
    ) {
      API.get(`/entities/abroad_registration?id=${homeCountry?.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  }, [resCity, resProvince, homeCity, homeProvince, resCountry, homeCountry, entitiesFilterByResidence]);

  useEffect(() => {
    setEntity(null);
    setGdEntity(null);
  }, [entitiesFilterByResidence]);

  // Recupero entità master GD filtrate per residenza/domicilio (comune + provincia).
  useEffect(() => {
    const isRes = entitiesFilterByResidence;
    const city = isRes ? resCity : homeCity;
    const province = isRes ? resProvince : homeProvince;

    if (city?.id && province?.id) {
      fetchGdEntities({ cityId: city.id, provinceId: province.id })
        .then(({ entities, empty }) => {
          setGdEntityList(entities);
          setGdEntityListEmpty(empty);
        })
        .catch(() => {
          setGdEntityList([]);
          setGdEntityListEmpty(false);
        });
    } else {
      setGdEntityList([]);
      setGdEntityListEmpty(false);
    }
  }, [resCity, resProvince, homeCity, homeProvince, entitiesFilterByResidence]);

  const [onlineUser, setOnlineUser] = useState(false);

  const handleChangeOnlineUser = () => {
    setOnlineUser(!onlineUser);
  };

  const [userNewsletter, setUserNewsletter] = useState(true);

  const handleChangeUserNewsletter = () => {
    setUserNewsletter(!userNewsletter);
  };

  // dynamic containers
  const [homeDynamicContainer, setHomeDynamicContainer] = useState(0);
  const [aliasDynamicContainer, setAliasDynamicContainer] = useState(0);

  // Domicile Dynamic Container Show/Hide
  useEffect(() => {
    if (domRes) {
      setHomeDynamicContainer(0);
      setEntitiesFilterByResidence(true);
    } else {
      setHomeDynamicContainer(1);
    }
  }, [domRes]);

  // GD Check Status
  useEffect(() => {
    if (isGdAvailable) {
      setGdCheck(true);
    } else {
      setGdCheck(false);
    }
  }, [isGdAvailable]);

  // Membership GD Checkbox
  const handleGdCheck = () => {
    setGdCheck((current) => !current);
  };

  // GD disponibile secondo la stessa regola di /join: età eleggibile 14–29 (isGdEligible),
  // non più il semplice "< 30".
  useEffect(() => {
    const age = birthDate ? dayjs().diff(dayjs(birthDate), 'year') : null;
    setIsGdAvailable(isGdEligibleAge(age));
  }, [birthDate]);

  // Date Select
  const handleDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setBirthDate(formattedDate);
  };

  // Domicile = Residence checkbox
  const HandleDomRes = () => {
    setHomeRegion('');
    setHomeProvince('');
    setHomeCity('');
    setHomeStreet('');
    setDomRes((current) => !current);
  };

  // Alias
  const [useAlias, setUseAlias] = useState(false);

  // Alias Dynamic Container Show/Hide
  useEffect(() => {
    if (useAlias) {
      setAliasDynamicContainer(1);
    } else {
      setAliasDynamicContainer(0);
    }
  }, [useAlias]);

  const [alias, setAlias] = useState(null);

  // close new user dialog
  const handleCloseNewUser = () => {
    setOpenNewUser(false);
    setResCountry(null);
    setResRegion('');
    setResProvince('');
    setResCity('');
    setResStreet('');
    setHomeCountry('');
    setHomeRegion('');
    setHomeProvince('');
    setHomeCity('');
    setHomeStreet('');
    setEntity(null);
    setSelectedEntity(null);
    setGdEntity(null);

    setErrors((prevErrors) =>
      Object.keys(prevErrors).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    );
  };

  useEffect(() => {
    if (domRes) {
      setHomeCity(resCity);
      setHomeProvince(resProvince);
      setHomeRegion(resRegion);
      setHomeCountry(resCountry);
    }
  }, [domRes, resCity, resProvince, resRegion, resCountry]);

  const getFirstApiError = (errorResponse) => {
    if (errorResponse?.errors && Object.keys(errorResponse.errors).length > 0) {
      const firstError = Object.values(errorResponse.errors)[0][0];
      return firstError;
    }

    if (errorResponse?.message) {
      return errorResponse.message;
    }

    // Caso generico
    return 'Si è verificato un errore';
  };

  // create new user
  const handleSubmitNewUser = (event, redirect = false) => {
    event.preventDefault();

    let data1;
    if (redirect) {
      const form = event.target.form;
      data1 = new FormData(form);
    } else {
      data1 = new FormData(event.currentTarget);
    }

    const genderValue = gender ? gender.gender.toLowerCase() : null;

    let userGender;

    if (genderValue === 'uomo') {
      userGender = 'male';
    } else if (genderValue === 'donna') {
      userGender = 'female';
    } else if (genderValue === 'altro') {
      userGender = 'other';
    } else {
      userGender = null;
    }

    const oData = {
      name: data1.get('name'),
      surname: data1.get('surname'),
      birth_date: birthDate,
      place_of_birth: birthPlace.id,
      res_equal_home: Number(domRes),
      home_country_id: domRes ? (resCountry ? resCountry.id : '') : homeCountry ? homeCountry.id : '',
      home_street: domRes ? resStreet : homeStreet,
      home_zip_code: domRes ? data1.get('res-cap') : data1.get('dom-cap'),
      call_code: data1.get('call_code'),
      allow_news: Number(userNewsletter),
      res_street: resStreet,
      fiscal_code: data1.get('fiscal-code').toUpperCase(),
      res_country_id: resCountry ? resCountry.id : '',
      entity_id: entity ? entity.id : '',
      user_online: Number(onlineUser),
      res_zip_code: data1.get('res-cap'),
      gd_check: Number(gdCheck),
      entity_master_gd: isGdUser && gdEntity ? gdEntity.id : null,
    };

    if (resCountry !== null && resCountry !== undefined && resCountry !== '' && resCountry.id === italy.id) {
      oData.res_city_id = resCity ? resCity.id : '';
      oData.res_province_id = resProvince ? resProvince.id : '';
      oData.res_region_id = resRegion ? resRegion.region_name : '';

      if (domRes) {
        oData.home_city_id = resCity ? resCity.id : '';
        oData.home_province_id = resProvince ? resProvince.id : '';
        oData.home_region_id = resRegion ? resRegion.region_name : '';
      }
    } else {
      oData.res_city_name = resCityName || '';
      oData.res_region_name = resRegionName || '';

      if (domRes) {
        oData.home_city_name = resCityName || '';
        oData.home_region_name = resRegionName || '';
      }
    }

    if (!domRes) {
      if (homeCountry !== null && homeCountry !== undefined && homeCountry !== '' && homeCountry.id === italy.id) {
        oData.home_city_id = homeCity ? homeCity.id : '';
        oData.home_province_id = homeProvince ? homeProvince.id : '';
        oData.home_region_id = homeRegion ? homeRegion.region_name : '';
      } else {
        oData.home_city_name = homeCityName || '';
        oData.home_region_name = homeRegionName || '';
      }
    }

    if (useAlias) {
      oData.gender = userGender;
      oData.name_alias = alias;
    }
    // aggiungi subito l'email a oData
    oData.email = data1.get('email') ? data1.get('email').replace(/\s+/g, '') : '';

    // modalità contact
    const isContactMode = mode === 'contact';
    const requiredFields = isContactMode ? ['email', 'entity_id'] : [];

    // validazione
    let itemsLeft = false;
    const newErrors = { ...errors };

    Object.keys(oData).forEach((key) => {
      const value = oData[key];

      if (isContactMode) {
        // solo email e entity_id sono obbligatori
        if (requiredFields.includes(key)) {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            itemsLeft = true;
            newErrors[key] = true;
            showPopup('warning', 'Inserisci i dati mancanti');
          } else {
            newErrors[key] = false;
          }
        } else {
          newErrors[key] = false; // tutti gli altri campi opzionali
        }
        return; // salta il resto
      }

      // validazione normale per mode === 'user'
      if (
        value === undefined ||
        value === null ||
        value === 'Invalid Date' ||
        (typeof value === 'string' && value.length === 0)
      ) {
        if (
          !(
            key === 'email' ||
            (key === 'home_country_id' && oData.res_equal_home) ||
            (key === 'home_region_id' && oData.res_equal_home) ||
            (key === 'home_province_id' && oData.res_equal_home) ||
            (key === 'home_city_id' && oData.res_equal_home) ||
            (key === 'home_street' && oData.res_equal_home) ||
            (key === 'home_zip_code' && oData.res_equal_home) ||
            key === 'gender' ||
            key === 'entity_master_gd'
          )
        ) {
          itemsLeft = true;
          newErrors[key] = true;
          showPopup('warning', 'Inserisci i dati mancanti');
        } else {
          newErrors[key] = false;
        }
      } else {
        newErrors[key] = false;
      }
    });

    // Entità GD obbligatoria solo se l'utente è GD (gdCheck + età eleggibile).
    if (isGdUser && !gdEntity) {
      itemsLeft = true;
      newErrors.entity_master_gd = true;
      showPopup('warning', 'Inserisci i dati mancanti');
    } else {
      newErrors.entity_master_gd = false;
    }

    setErrors(newErrors);

    if (!itemsLeft) {
      delete oData.home_region_id;
      delete oData.res_region_id;

      if (phoneNumber.length > 0) {
        oData.phone = phoneNumber;
      }

      if (mode === 'user') {
        if (birthDate && dayjs(birthDate).isBefore(dayjs(fourteenYearsAgo))) {
          API.post('/admin/users/offline', oData)
            .then((response) => {
              if (response.status === 201) {
                if (response.data !== '') {
                  setPopUpText(response?.data?.message ?? 'Registrazione avvenuta con successo');
                  showPopup(true);
                  getUsers();
                  handleCloseNewUser();
                  setResCountry(null);
                  setResRegion('');
                  setResProvince('');
                  setResCity('');
                  setResStreet('');
                  setHomeCountry('');
                  setHomeRegion('');
                  setHomeProvince('');
                  setHomeCity('');
                  setHomeStreet('');
                  setEntity('');
                }
              }

              const userId = response.data.id;
              if (redirect) {
                navigate('/admin/users/user-details', { state: { userID: userId } });
              }

              if (response.status === 205) {
                setPopUpText('Utente già registrato');
                showPopup(false);
              }
            })
            .catch((error) => {
              const errorMessage = getFirstApiError(error.response?.data);
              setPopUpText(errorMessage);
              showPopup(false);
            });
        } else if (!birthDate) {
          setPopUpText('La data di nascita è obbligatoria');
          showPopup(false);
        } else {
          setPopUpText("L'utente deve avere compiuto almeno i 14 anni di età");
          showPopup(false);
        }
      } else {
        // Se mode === 'contact', bypassa il controllo dell'età
        API.post('/admin/contact/offline', oData)
          .then((response) => {
            if (response.status === 201) {
              if (response.data !== '') {
                setPopUpText(response?.data?.message ?? 'Registrazione avvenuta con successo');
                showPopup(true);
                getUsers();
                handleCloseNewUser();
                setResCountry(null);
                setResRegion('');
                setResProvince('');
                setResCity('');
                setResStreet('');
                setHomeCountry('');
                setHomeRegion('');
                setHomeProvince('');
                setHomeCity('');
                setHomeStreet('');
                setEntity('');
                setSelectedEntity(null);
              }
            }

            const userId = response.data.user.id;
            if (redirect) {
              navigate('/admin/users/user-details', { state: { userID: userId } });
            }

            if (response.status === 205) {
              setPopUpText('Utente già registrato');
              showPopup(false);
            }
          })
          .catch((error) => {
            const errorMessage = getFirstApiError(error.response?.data);
            setPopUpText(errorMessage);
            showPopup(false);
          });
      }
    }
  };

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  const [selectedEntity, setSelectedEntity] = useState(null);

  const getSelectedEntity = (selectedEntity) => {
    setSelectedEntity(selectedEntity);
  };

  useEffect(() => {
    if (selectedEntity) {
      setEntity(selectedEntity);
    }
  }, [selectedEntity]);

  return (
    <Dialog fullWidth maxWidth="lg" open={openNewUser} onClose={() => {}}>
      <DialogTitle>{mode === 'user' ? 'Registrazione nuovo utente' : 'Registrazione nuovo contatto'}</DialogTitle>
      <DialogContent>
        {registerApiCalled ? (
          <form method="post" id="register-form" onSubmit={handleSubmitNewUser}>
            <Box>
              <Grid sx={{ paddingTop: '20px' }} justifyContent="center" alignItems="center" container spacing={3}>
                {/* Name */}
                <Grid item xs={12} md={6} lg={5}>
                  <CustomTextField error={errors.name} id="register-name" name="name" label="Nome" />
                </Grid>

                {/* Surname */}
                <Grid item xs={12} md={6} lg={5}>
                  <CustomTextField error={errors.surname} id="register-surname" name="surname" label="Cognome" />
                </Grid>

                <Grid item xs={12} lg={10}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useAlias}
                        onChange={(e) => {
                          setUseAlias(e.target.checked);
                        }}
                      />
                    }
                    label="Utilizza nome alias"
                  />
                </Grid>

                {useAlias && (
                  <Grid item xs={12} lg={10}>
                    <Grid container spacing={3} style={{ opacity: aliasDynamicContainer, transition: 'opacity .5s' }}>
                      <Grid item xs={8}>
                        <CustomTextField
                          error={errors.name_alias}
                          id="register-alias"
                          name="alias"
                          label="Alias"
                          onChange={(e) => setAlias(e.currentTarget.value)}
                        />
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={4}>
                        <CustomAutocomplete
                          required={useAlias}
                          id="register-gender"
                          options={genderList}
                          isOptionEqualToValue={(option, value) => option.gender === value.gender}
                          getOptionLabel={(option) => option.gender}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.gender}>
                              {option.gender}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            setGender(value);
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Sesso" name="gender" variant="outlined" />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Alert severity="warning">
                          Specificare un "Nome Alias" e il "Sesso" solo se si è in una fase di transizione di genere.
                        </Alert>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* Date of Birth */}
                <Grid item xs={12} md={6} lg={5}>
                  <Box>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker
                        error={errors.birth_date}
                        label={mode === 'contact' ? 'Data di Nascita' : 'Data di Nascita *'}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        maxDate={fourteenYearsAgo}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6} lg={5}>
                  <CustomAutocomplete
                    options={birthPlacesList}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    id="register-birthPlace"
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option?.province_abbr?.length > 0 ? `${option.name} (${option?.province_abbr})` : option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setBirthPlace(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors.place_of_birth}
                        label={mode === 'contact' ? 'Luogo di Nascita' : 'Luogo di Nascita *'}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} lg={10}>
                  <Alert severity="warning">
                    Attenzione! Verrà verificata la compatibilità del codice fiscale con i tuoi dati anagrafici. Se
                    riscontri problemi contatta l'helpdesk all'indirizzo tesseramento@partitodemocratico.it
                  </Alert>
                </Grid>

                {/* Tax ID */}
                <Grid item xs={12} md={6} lg={4}>
                  <CustomTextField
                    error={errors.fiscal_code}
                    id="register-fiscal-code"
                    name="fiscal-code"
                    label="Codice Fiscale"
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6} lg={4}>
                  <CustomTextField
                    error={errors.email}
                    id="register-email"
                    name="email"
                    label={mode === 'contact' ? 'Email *' : 'Email'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip arrow title="Riceverai un codice di verifica" placement="top">
                            <InfoOutlinedIcon sx={{ color: '#2f58ca' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Call Code */}
                <Grid item xs={4} md={3} lg={2}>
                  <CustomAutocomplete
                    options={resCountriesList}
                    id="register-phone-prefix"
                    getOptionLabel={(option) => option.call_code}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name} {option.call_code}
                      </Box>
                    )}
                    value={callCode}
                    filterOptions={(options, state) => {
                      const newOptions = [];
                      options.forEach((element) => {
                        if (
                          element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                          element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                        ) {
                          newOptions.push(element);
                        }
                      });
                      return newOptions;
                    }}
                    isOptionEqualToValue={(option, value) => callCode && option.id === value.id}
                    onChange={(e, value) => setCallCode(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors.call_code}
                        label="Prefisso"
                        name="call_code"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={8} md={5} lg={4}>
                  <CustomTextField
                    type="tel"
                    id="register-phone-number"
                    name="phone-number"
                    label="Cellulare"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip arrow title="Riceverai un codice di verifica" placement="top">
                            <InfoOutlinedIcon sx={{ color: '#2f58ca' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) => validatePhoneNumber(event, setPhoneNumber)}
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={12} md={4} lg={3}>
                  <CustomAutocomplete
                    required
                    options={resCountriesList.filter(
                      (country) =>
                        (!resRegion || country.id === resRegion.country_id) &&
                        (!resProvince || country.id === resProvince.country_id)
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name}
                    id="register-res-country"
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    value={resCountry}
                    onChange={(e, value) => setResCountry(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors.res_country_id}
                        label={mode === 'contact' ? 'Paese' : 'Paese *'}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                {/* Region */}
                <Grid item xs={12} md={6} lg={3}>
                  <CustomAutocomplete
                    freeSolo
                    forcePopupIcon={resCountry === italy}
                    options={resRegionList.filter(
                      (region) =>
                        (!resProvince || region.region_name === resProvince.region_name) &&
                        (!resCountry || region.country_id === resCountry.id)
                    )}
                    isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                    getOptionLabel={(option) => option.region_name}
                    id="register-res-region"
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.region_name}>
                        {option.region_name}
                      </Box>
                    )}
                    onChange={(e, value) => setResRegion(value)}
                    onInputChange={(e, value) => {
                      setResRegionName(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors.res_region_id || errors.res_region_name}
                        label={mode === 'contact' ? 'Regione' : 'Regione *'}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                {/* Province */}
                <Grid item xs={12} md={6} lg={3}>
                  <CustomAutocomplete
                    required
                    freeSolo
                    forcePopupIcon={resCountry === italy}
                    options={resProvincesList.filter(
                      (province) =>
                        (!resRegion || province.region_name === resRegion.region_name) &&
                        (!resCity || province.id === resCity.province_id) &&
                        (!resCountry || province.country_id === resCountry.id)
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    id="register-res-province"
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setResProvince(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors.res_province_id}
                        label={mode === 'contact' ? 'Provincia' : 'Provincia *'}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} md={6} lg={3}>
                  <CustomAutocomplete
                    required
                    freeSolo
                    forcePopupIcon={resCountry === italy}
                    options={resCitiesList.filter((city) => !resProvince || city.province_id === resProvince.id)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    id="register-res-city"
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setResCity(value)}
                    onInputChange={(e, value) => {
                      setResCityName(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors.res_city_id || errors.res_city_name}
                        label={mode === 'contact' ? 'Comune' : 'Comune *'}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                {/* Street */}
                <Grid item xs={12} md={6} lg={4}>
                  <CustomTextField
                    error={errors.res_street}
                    id="register-res-address"
                    name="res-adress"
                    onChange={(e) => {
                      setResStreet(e.target.value);
                    }}
                    label="Indirizzo"
                  />
                </Grid>

                {/* CAP + Entità PD + Entità GD: riga dedicata (container annidato, somma interna 12) */}
                <Grid item xs={12} lg={10}>
                  <Grid container spacing={3}>
                    {/* Zip Code */}
                    <Grid item xs={12} md={4} lg={3}>
                      <CustomTextField error={errors.res_zip_code} id="register-res-cap" name="res-cap" label="CAP" />
                    </Grid>

                    {/* Entity PD */}
                    {mode === 'user' ? (
                      <Grid item xs={12} md={4} lg={4.5}>
                        <CustomAutocomplete
                          id="register-circolo"
                          disabled={disableEntity}
                          options={EntitiesList}
                          value={entity}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            setEntity(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.entity_id}
                              label="Circolo di Appartenenza *"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={4} lg={4.5}>
                        <CustomTextField
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                <Tooltip title="Modifica" placement="left" arrow>
                                  <EditIcon
                                    onClick={() => {
                                      setOpenEntitiesFilter(!openEntitiesFilter);
                                    }}
                                  />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          value={selectedEntity ? selectedEntity?.name : ''}
                          label="Entità di Appartenenza"
                        />

                        <EntitiesDialog
                          openDialog={openEntitiesFilter}
                          closeDialog={handleCloseEntitiesFilterDialog}
                          outputEntity={getSelectedEntity}
                          context={'contact'}
                        />
                      </Grid>
                    )}

                    {/* Entità master GD — sempre visibile; attiva e obbligatoria solo se l'utente è GD */}
                    <Grid item xs={12} md={4} lg={4.5}>
                      <GdMasterEntitySelect
                        value={gdEntity}
                        onChange={setGdEntity}
                        options={gdEntityList}
                        error={errors.entity_master_gd}
                        disabled={!isGdUser || gdEntityListEmpty}
                        label={isGdUser ? 'Entità di iscrizione GD *' : 'Entità di iscrizione GD'}
                      />
                      {isGdUser && gdEntityListEmpty && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          Nessuna entità disponibile per la registrazione ai Giovani Democratici
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {emptyEntitiesList && (
                  <Grid item xs={12} lg={10}>
                    <Alert severity="error">Attenzione! Nessuna entità disponibile per la registrazione</Alert>
                  </Grid>
                )}

                <Grid item xs={12} lg={10}>
                  <Grid container spaging={3}>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        sx={{ paddingTop: '12px' }}
                        control={<Switch checked={Boolean(onlineUser)} onClick={handleChangeOnlineUser} />}
                        label={onlineUser ? 'Utente online' : 'Utente offline'}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        sx={{ paddingTop: '12px' }}
                        control={<Switch checked={Boolean(userNewsletter)} onClick={handleChangeUserNewsletter} />}
                        label={userNewsletter ? 'Riceve comunicazioni' : 'Non riceve comunicazioni'}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  {/* Checkboxes Section + Dynamic Containers */}
                  <Stack direction="row">
                    <Grid justifyContent="center" alignItems="center" container spacing={2}>
                      <Grid item xs={12} lg={10}>
                        <FormControlLabel
                          control={<Checkbox checked={domRes} onChange={HandleDomRes} />}
                          label="Il domicilio corrisponde con la residenza"
                        />
                      </Grid>

                      <Grid item xs={12} lg={10} sx={{ display: domRes ? 'none' : 'flex' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={entitiesFilterByResidence}
                              onChange={() => setEntitiesFilterByResidence(!entitiesFilterByResidence)}
                              disabled={domRes}
                            />
                          }
                          label={
                            entitiesFilterByResidence
                              ? 'Filtra i circoli rispetto alla residenza'
                              : 'Filtra i circoli rispetto al domicilio'
                          }
                        />
                      </Grid>

                      {/* Domicile != Residence */}
                      {domRes === false && (
                        <Grid style={{ opacity: homeDynamicContainer, transition: 'opacity 1s' }} item xs={12}>
                          <Grid container justifyContent="center" alignItems="center" spacing={3}>
                            <Grid item xs={12} lg={10}>
                              <Typography sx={{ fontWeight: '500', textDecoration: 'underline' }}>
                                Inserisci di seguito le informazioni relative al domicilio
                              </Typography>
                            </Grid>

                            {/* Domicile Country */}
                            <Grid item xs={12} md={4} lg={3}>
                              <CustomAutocomplete
                                options={homeCountriesList.filter(
                                  (country) =>
                                    (!homeRegion || country.id === homeRegion.country_id) &&
                                    (!homeProvince || country.id === homeProvince.country_id)
                                )}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                id="register-dom-country"
                                renderOption={(props, option) => (
                                  <Box component="li" id="country-id" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                value={homeCountry}
                                onChange={(e, value) => setHomeCountry(value)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_country_id}
                                    label="Paese"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>

                            {/* Domicile Region */}
                            <Grid item xs={12} md={4} lg={3}>
                              <CustomAutocomplete
                                options={homeRegionsList.filter(
                                  (region) =>
                                    (!homeProvince || region.region_name === homeProvince.region_name) &&
                                    (!homeCountry || region.country_id === homeCountry.id)
                                )}
                                freeSolo
                                forcePopupIcon={homeCountry === italy}
                                isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                                getOptionLabel={(option) => option.region_name}
                                id="register-dom-region"
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.region_name}>
                                    {option.region_name}
                                  </Box>
                                )}
                                onChange={(e, value) => setHomeRegion(value)}
                                onInputChange={(e, value) => {
                                  setHomeRegionName(value);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_region_id || errors.home_region_name}
                                    label="Regione"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>

                            {/* Domicile Province */}
                            <Grid item xs={12} md={4} lg={4}>
                              <CustomAutocomplete
                                options={homeProvincesList.filter(
                                  (province) =>
                                    (!homeRegion || province.region_name === homeRegion.region_name) &&
                                    (!homeCity || province.id === homeCity.province_id) &&
                                    (!homeCountry || province.country_id === homeCountry.id)
                                )}
                                freeSolo
                                forcePopupIcon={homeCountry === italy}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                id="register-dom-province"
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                onChange={(e, value) => setHomeProvince(value)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_province_id}
                                    label="Provincia"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>

                            {/* Domicile City */}
                            <Grid item xs={12} md={5} lg={4}>
                              <CustomAutocomplete
                                options={homeCitiesList.filter((city) =>
                                  homeProvince ? city.province_id === homeProvince.id : true
                                )}
                                freeSolo
                                forcePopupIcon={homeCountry === italy}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                id="register-dom-city"
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                onChange={(e, value) => setHomeCity(value)}
                                onInputChange={(e, value) => {
                                  setHomeCityName(value);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_city_id || errors.home_city_name}
                                    label="Comune"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>

                            {/* Domicile Street */}
                            <Grid item xs={12} md={5} lg={4}>
                              <CustomTextField
                                id="register-dom-adress"
                                onChange={(e) => {
                                  setHomeStreet(e.target.value);
                                }}
                                name="dom-adress"
                                label="Indirizzo"
                                error={errors.home_street}
                              />
                            </Grid>

                            {/* Domicile Zip Code */}
                            <Grid item xs={12} md={2}>
                              <CustomTextField
                                error={errors.home_zip_code}
                                id="register-dom-cap"
                                name="dom-cap"
                                label="CAP"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Stack>
                </Grid>

                <Grid item xs={12} lg={10}>
                  <FormControlLabel
                    control={<Checkbox disabled={!isGdAvailable} checked={gdCheck} onChange={handleGdCheck} />}
                    label="Iscrizione ai Giovani Democratici"
                    labelPlacement="end"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Submit Step1 */}
            <Stack direction="row" alignItems="center" justifyContent="end" gap="12px" id="register-submit-button">
              <DefaultOutlinedButton onClick={handleCloseNewUser}>Annulla</DefaultOutlinedButton>
              <DefaultContainedButton
                type="submit"
                disabled={emptyEntitiesList}
                colorVariant={mode === 'user' ? 'red' : 'blue'}
              >
                Crea
              </DefaultContainedButton>

              <DefaultContainedButton
                type="submit"
                onClick={(event) => handleSubmitNewUser(event, true)}
                disabled={emptyEntitiesList}
                colorVariant={mode === 'user' ? 'red' : 'blue'}
              >
                Crea e vai alla scheda
              </DefaultContainedButton>
            </Stack>
          </form>
        ) : (
          LoadingUI()
        )}
        {!registerApiCalled && (
          <DialogActions>
            <DefaultOutlinedButton onClick={handleCloseNewUser}>Chiudi</DefaultOutlinedButton>
          </DialogActions>
        )}
      </DialogContent>
    </Dialog>
  );
}
